"use client";

import { useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { marketFinderSubmissionsService } from "@/features/submissions/services/submissions.service";
import type { CommunicationsSummary } from "@/features/submissions/types/submissions.types";
import {
  getCommunicationsSseConnected,
  subscribeCommunicationsSse,
} from "@/features/submissions/hooks/communicationsSseState";
import { useUnderwriterWorkingSet } from "@/features/submissions/hooks/underwriterWorkingSet";

/**
 * Aggregate communication counts for badges (GIQS-145): sidebar nav badge,
 * submissions-grid status column, and the Communication tab badge. Refreshed
 * by SSE pushes (GIQS-162); the 60s poll only runs while the stream is
 * disconnected, as a fallback.
 */
export function useCommunicationsSummary(enabled = true) {
  const sseConnected = useSyncExternalStore(
    subscribeCommunicationsSse,
    getCommunicationsSseConnected,
    () => false,
  );
  // The persisted underwriter working set narrows every badge (server-side
  // re-validated). Part of the key so changing the set refetches.
  const workingSet = useUnderwriterWorkingSet();
  return useQuery<CommunicationsSummary>({
    queryKey: ["market-finder-communications-summary", workingSet],
    queryFn: () =>
      marketFinderSubmissionsService.getCommunicationsSummary(workingSet),
    enabled,
    staleTime: 30_000,
    refetchInterval: sseConnected ? false : 60_000,
    retry: 1,
  });
}
